import { useState, useCallback, useEffect } from 'react';
import { IMessage } from '../../../dataProvider/DataProvider';
import { _dataProvider } from '../../../dataProvider/DataProvider';
import { getJwtObj } from '../../../SharedCommon/utils';
import i18n from '../../../i18n';
import useToast from '../../../SharedCommon/useToast';

interface IUseBatchSelectionOptions {
  groupId: number;
  messages: IMessage[];
  getCurrentMessages: () => IMessage[];
  checkCurrentUserIsLeader: () => boolean;
  getMessageTextForCollection: (
    msg: any,
    groupId?: number,
    skipSolidification?: boolean
  ) => Promise<{ text: string; attachment?: any }>;
  removeCollectionMessage: (type: 'user' | 'group', msg: any, silent?: boolean) => Promise<boolean>;
  showAlertDialog: (message: string) => void;
  setMessages: React.Dispatch<React.SetStateAction<IMessage[]>>;
  onBatchSelectModeChange?: (isBatchMode: boolean, selectedCount: number, hasOthersMessages?: boolean) => void;
  onShareTargetSelectRequest?: (
    method: 'one-by-one' | 'package',
    selectedMessages: any[],
    sourceGroupId: number
  ) => void;
  setConfirmDialog: React.Dispatch<
    React.SetStateAction<{
      show: boolean;
      title: string;
      content: string;
      onConfirm: () => void;
      onCancel?: () => void;
    }>
  >;
  // setShowShareMethodDialog: React.Dispatch<React.SetStateAction<boolean>>; // 暂时隐藏转发功能
}

interface IUseBatchSelectionReturn {
  isBatchSelectMode: boolean;
  setIsBatchSelectMode: React.Dispatch<React.SetStateAction<boolean>>;
  selectedMessageIds: Set<number>;
  setSelectedMessageIds: React.Dispatch<React.SetStateAction<Set<number>>>;
  toggleMessageSelection: (messageId: number) => void;
  exitBatchSelectMode: () => void;
  selectMsg: (msg: any) => void;
  shareMsg: (msg: any) => void;
  handleBatchGroupCollection: () => Promise<void>;
  handleBatchRemoveGroupCollection: () => Promise<void>;
  handleBatchMyCollection: () => Promise<void>;
  handleBatchRemoveMyCollection: () => Promise<void>;
  handleBatchDelete: () => Promise<void>;
  // handleBatchShare: () => void; // 暂时隐藏转发功能
  // hasMergedForwardMessage: () => boolean; // 暂时隐藏转发功能
  // handleShareMethod: (method: 'one-by-one' | 'package') => void; // 暂时隐藏转发功能
  uncollectSelectedMessages: (target?: 'user' | 'group') => Promise<void>;
}

export const useBatchSelection = ({
  groupId,
  getCurrentMessages,
  checkCurrentUserIsLeader,
  getMessageTextForCollection,
  removeCollectionMessage,
  showAlertDialog,
  setMessages,
  onBatchSelectModeChange,
  onShareTargetSelectRequest,
  setConfirmDialog
  // setShowShareMethodDialog // 暂时隐藏转发功能
}: IUseBatchSelectionOptions): IUseBatchSelectionReturn => {
  const [isBatchSelectMode, setIsBatchSelectMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<number>>(new Set());
  const { toast } = useToast();

  const toggleMessageSelection = useCallback((messageId: number) => {
    setSelectedMessageIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  }, []);

  const exitBatchSelectMode = useCallback(() => {
    setIsBatchSelectMode(false);
    setSelectedMessageIds(new Set());
  }, []);

  const selectMsg = useCallback((msg: any) => {
    if (!msg || !msg.id) {
      return;
    }
    setIsBatchSelectMode(true);
    setSelectedMessageIds(new Set([msg.id]));
  }, []);

  const shareMsg = useCallback(
    (msg: any) => {
      if (!msg) {
        return;
      }

      if (!onShareTargetSelectRequest) {
        toast({
          intent: 'error',
          msg: i18n.t('Chatroom.ShareNotImplemented')
        });
        return;
      }

      // 合并转发消息也统一走 one-by-one，以保持结构
      onShareTargetSelectRequest('one-by-one', [msg], groupId);
    },
    [groupId, onShareTargetSelectRequest, toast]
  );

  const getMessageTime = (msg: any): number => {
    if (msg.time && typeof msg.time === 'number' && msg.time > 0) {
      return msg.time;
    }
    if (msg.createdAt) {
      try {
        const createdAt =
          typeof msg.createdAt === 'string' || typeof msg.createdAt === 'number'
            ? new Date(msg.createdAt)
            : (msg.createdAt as Date);
        return Math.floor(createdAt.getTime() / 1000);
      } catch {
        return Math.floor(Date.now() / 1000);
      }
    }
    return Math.floor(Date.now() / 1000);
  };

  const buildCollectionBatchData = useCallback(
    async (messages: any[], currentGroupId: number) => {
      // IMPORTANT: Collection operations MUST use the actual message_id from the current group.
      // For forwarded messages, msg.id should be the NEW message_id in the target group, NOT the original message_id.

      const validMessages = messages.filter(
        (msg) =>
          msg?.id !== undefined &&
          msg.id !== null &&
          msg?.user &&
          typeof msg.user === 'object' &&
          typeof msg.user.id === 'number'
      );

      const messageIdSet = new Set<number | string>();

      // 批量收藏时跳过附件固化以提升速度，只序列化已有的消息数据
      const messageTextPromises = validMessages.map((msg: any) =>
        getMessageTextForCollection(msg, currentGroupId, true)
      );
      const messageTexts = await Promise.all(messageTextPromises);

      const batchData = validMessages
        .map((msg: any, index: number) => {
          const { text } = messageTexts[index];
          const messageTime = getMessageTime(msg);
          const messageIdToUse = msg.id;

          if (!messageIdToUse) {
            return null;
          }

          return {
            message: text || '',
            user: msg.user.id,
            time: messageTime,
            messageId: messageIdToUse
          };
        })
        .filter((item): item is NonNullable<typeof item> => {
          if (!item) {
            return false;
          }
          if (
            typeof item.message !== 'string' ||
            typeof item.user !== 'number' ||
            typeof item.time !== 'number' ||
            item.time <= 0
          ) {
            return false;
          }
          if (item.messageId === undefined || item.messageId === null) {
            return false;
          }

          const msgId = item.messageId;
          if (messageIdSet.has(msgId)) {
            return false;
          }
          messageIdSet.add(msgId);
          return true;
        });

      return batchData;
    },
    [getMessageTextForCollection]
  );

  const parseCollectionResult = (result: any, totalItems: number): { successCount: number; failCount: number } => {
    if (typeof result.successCount === 'number' || typeof result.failCount === 'number') {
      return {
        successCount: typeof result.successCount === 'number' ? result.successCount : 0,
        failCount: typeof result.failCount === 'number' ? result.failCount : 0
      };
    }
    console.warn(
      '[Collection] Using legacy result format. Consider migrating to new format with successCount/failCount.'
    );
    return {
      successCount: result.success ? totalItems : 0,
      failCount: result.success ? 0 : totalItems
    };
  };

  const handleBatchGroupCollection = useCallback(async () => {
    if (!checkCurrentUserIsLeader()) {
      toast({ intent: 'error', msg: i18n.t('Chatroom.OnlyLeaderCanPost') });
      return;
    }

    const selectedMessages = getCurrentMessages().filter((msg) => selectedMessageIds.has(msg.id));
    if (selectedMessages.length === 0) {
      return;
    }

    const batchData = await buildCollectionBatchData(selectedMessages, groupId);

    if (batchData.length === 0) {
      showAlertDialog(i18n.t('HandleFailed'));
      return;
    }

    try {
      const result = await _dataProvider.batchUpdateCollectionMessage(groupId, 'group', batchData);

      const { successCount, failCount } = parseCollectionResult(result, batchData.length);

      if (successCount > 0) {
        if (failCount > 0) {
          showAlertDialog(
            i18n.t('Chatroom.BatchCollectionPartialSuccess', {
              success: successCount,
              total: batchData.length,
              fail: failCount
            }) || `成功收藏 ${successCount} 条，失败 ${failCount} 条，共 ${batchData.length} 条`
          );
        } else {
          showAlertDialog(i18n.t('Chatroom.BatchCollectionSuccess', { count: successCount }));
        }
        exitBatchSelectMode();
      } else {
        showAlertDialog(i18n.t('Chatroom.BatchCollectionFailed', { count: failCount }));
      }
    } catch (error) {
      console.error('[Collection] Error in handleBatchGroupCollection:', error);
      showAlertDialog(i18n.t('Chatroom.BatchCollectionFailed', { count: batchData.length }));
    }
  }, [
    selectedMessageIds,
    getCurrentMessages,
    exitBatchSelectMode,
    groupId,
    checkCurrentUserIsLeader,
    showAlertDialog,
    buildCollectionBatchData,
    toast
  ]);

  const handleBatchRemoveGroupCollection = useCallback(async () => {
    if (!checkCurrentUserIsLeader()) {
      toast({ intent: 'error', msg: i18n.t('Chatroom.OnlyLeaderCanPost') });
      return;
    }

    const selectedMessages = getCurrentMessages().filter((msg) => selectedMessageIds.has(msg.id));
    if (selectedMessages.length === 0) {
      return;
    }

    let failCount = 0;

    try {
      for (const msg of selectedMessages) {
        if (msg?.id) {
          const success = await removeCollectionMessage('group', msg, true);
          if (!success) {
            failCount++;
          }
        }
      }

      if (failCount > 0) {
        showAlertDialog(i18n.t('Chatroom.BatchRemoveCollectionFailed', { count: failCount }));
      }
    } catch {
      showAlertDialog(i18n.t('Chatroom.BatchRemoveCollectionFailed', { count: selectedMessages.length }));
    }

    exitBatchSelectMode();
  }, [
    selectedMessageIds,
    getCurrentMessages,
    exitBatchSelectMode,
    removeCollectionMessage,
    checkCurrentUserIsLeader,
    showAlertDialog,
    toast
  ]);

  const handleBatchRemoveMyCollection = useCallback(async () => {
    const selectedMessages = getCurrentMessages().filter((msg) => selectedMessageIds.has(msg.id));
    if (selectedMessages.length === 0) {
      return;
    }

    let failCount = 0;

    try {
      for (const msg of selectedMessages) {
        if (msg?.id) {
          const success = await removeCollectionMessage('user', msg, true);
          if (!success) {
            failCount++;
          }
        }
      }

      if (failCount > 0) {
        showAlertDialog(i18n.t('Chatroom.BatchRemoveCollectionFailed', { count: failCount }));
      }
    } catch {
      showAlertDialog(i18n.t('Chatroom.BatchRemoveCollectionFailed', { count: selectedMessages.length }));
    }

    exitBatchSelectMode();
  }, [selectedMessageIds, getCurrentMessages, exitBatchSelectMode, removeCollectionMessage, showAlertDialog]);

  const uncollectSelectedMessages = useCallback(
    async (target?: 'user' | 'group'): Promise<void> => {
      const selectedMessages = getCurrentMessages().filter((msg) => selectedMessageIds.has(msg.id));
      if (selectedMessages.length === 0) {
        return;
      }

      let actualTarget: 'user' | 'group' = target || 'user';
      if (!target && selectedMessages.length > 0) {
        const firstMsg = selectedMessages[0] as any;
        if (firstMsg.target === 'user' || firstMsg.target === 'group') {
          actualTarget = firstMsg.target;
        } else if (firstMsg.collectionType === 'user' || firstMsg.collectionType === 'group') {
          actualTarget = firstMsg.collectionType;
        }
      }

      if (actualTarget === 'group' && !checkCurrentUserIsLeader()) {
        toast({ intent: 'error', msg: i18n.t('Chatroom.OnlyLeaderCanPost') });
        return;
      }

      const messageIds = selectedMessages
        .filter((msg) => msg?.id !== undefined && msg.id !== null)
        .map((msg) => msg.id)
        .filter((id): id is number => typeof id === 'number');

      if (messageIds.length === 0) {
        showAlertDialog(i18n.t('HandleFailed'));
        return;
      }

      try {
        const success = await _dataProvider.batchRemoveCollectionMessages(groupId, actualTarget, messageIds);
        const count = messageIds.length;

        if (success) {
          showAlertDialog(i18n.t('Chatroom.BatchRemoveCollectionSuccess', { count }));
          exitBatchSelectMode();
        } else {
          showAlertDialog(i18n.t('Chatroom.BatchRemoveCollectionFailed', { count }));
        }
      } catch {
        showAlertDialog(i18n.t('Chatroom.BatchRemoveCollectionFailed', { count: messageIds.length }));
      }
    },
    [
      selectedMessageIds,
      getCurrentMessages,
      groupId,
      checkCurrentUserIsLeader,
      showAlertDialog,
      toast,
      exitBatchSelectMode
    ]
  );

  const handleBatchMyCollection = useCallback(async () => {
    const selectedMessages = getCurrentMessages().filter((msg) => selectedMessageIds.has(msg.id));
    if (selectedMessages.length === 0) {
      return;
    }

    const batchData = await buildCollectionBatchData(selectedMessages, groupId);

    if (batchData.length === 0) {
      showAlertDialog(i18n.t('HandleFailed'));
      return;
    }

    try {
      const result = await _dataProvider.batchUpdateCollectionMessage(groupId, 'user', batchData);

      const { successCount, failCount } = parseCollectionResult(result, batchData.length);

      if (successCount > 0) {
        if (failCount > 0) {
          showAlertDialog(
            i18n.t('Chatroom.BatchCollectionPartialSuccess', {
              success: successCount,
              total: batchData.length,
              fail: failCount
            }) || `成功收藏 ${successCount} 条，失败 ${failCount} 条，共 ${batchData.length} 条`
          );
        } else {
          showAlertDialog(i18n.t('Chatroom.BatchCollectionSuccess', { count: successCount }));
        }
        exitBatchSelectMode();
      } else {
        showAlertDialog(i18n.t('Chatroom.BatchCollectionFailed', { count: failCount }));
      }
    } catch (error) {
      console.error('[Collection] Error in handleBatchMyCollection:', error);
      showAlertDialog(i18n.t('Chatroom.BatchCollectionFailed', { count: batchData.length }));
    }
  }, [selectedMessageIds, getCurrentMessages, exitBatchSelectMode, groupId, showAlertDialog, buildCollectionBatchData]);

  const handleBatchDeleteInternal = useCallback(
    async (messagesToDelete: any[]) => {
      if (messagesToDelete.length === 0) {
        return;
      }

      const messageIds = messagesToDelete.map((msg) => msg.id);
      const chunkSize = 20;
      let failCount = 0;
      const deletedIds: (number | string)[] = [];

      for (let i = 0; i < messageIds.length; i += chunkSize) {
        const chunk = messageIds.slice(i, i + chunkSize);
        const success = await _dataProvider.batchDeleteMessages(groupId, chunk);
        if (success) {
          deletedIds.push(...chunk);
        } else {
          failCount += chunk.length;
        }
      }

      if (deletedIds.length > 0) {
        const deletedSet = new Set(deletedIds);
        setMessages((prevMessages) => prevMessages.filter((m) => !deletedSet.has(m.id)));
      }

      if (failCount > 0) {
        showAlertDialog(i18n.t('Chatroom.BatchDeleteFailed', { count: failCount }));
      }

      exitBatchSelectMode();
    },
    [groupId, exitBatchSelectMode, showAlertDialog, setMessages]
  );

  const handleBatchDelete = useCallback(async () => {
    const selectedMessages = getCurrentMessages().filter((msg) => selectedMessageIds.has(msg.id));

    const currentUserId = getJwtObj().userId;
    const currentUserIsLeader = checkCurrentUserIsLeader();

    let messagesToDelete = selectedMessages;
    if (!currentUserIsLeader) {
      messagesToDelete = selectedMessages.filter((msg) => msg.user && msg.user.id === currentUserId);

      const othersMessages = selectedMessages.filter((msg) => msg.user && msg.user.id !== currentUserId);
      if (othersMessages.length > 0) {
        if (messagesToDelete.length === 0) {
          return;
        }
      }
    }

    if (messagesToDelete.length === 0) {
      return;
    }

    setConfirmDialog({
      show: true,
      title: i18n.t('GroupScreen.DeleteGroupsConfirmTitle'),
      content: i18n.t('Chatroom.ConfirmDelete', { count: messagesToDelete.length }),
      onConfirm: () => {
        setConfirmDialog((prev) => ({ ...prev, show: false }));
        handleBatchDeleteInternal(messagesToDelete);
      },
      onCancel: () => {
        setConfirmDialog((prev) => ({ ...prev, show: false }));
      }
    });
  }, [selectedMessageIds, getCurrentMessages, checkCurrentUserIsLeader, handleBatchDeleteInternal, setConfirmDialog]);

  // 暂时隐藏转发功能
  // const hasMergedForwardMessage = useCallback(() => {
  //   const selectedMessages = getCurrentMessages().filter((msg) => selectedMessageIds.has(msg.id));
  //   return selectedMessages.some((msg) => {
  //     const msgAny = msg as any;
  //     return msgAny.type === 'mergedForward' || msgAny.type === 5;
  //   });
  // }, [selectedMessageIds, getCurrentMessages]);

  // const handleBatchShare = useCallback(() => {
  //   const selectedMessages = getCurrentMessages().filter((msg) => selectedMessageIds.has(msg.id));
  //   if (selectedMessages.length === 0) {
  //     return;
  //   }
  //   setShowShareMethodDialog(true);
  // }, [selectedMessageIds, getCurrentMessages, setShowShareMethodDialog]);

  // const handleShareMethod = useCallback(
  //   (method: 'one-by-one' | 'package') => {
  //     if (method === 'package' && hasMergedForwardMessage()) {
  //       return;
  //     }

  //     setShowShareMethodDialog(false);
  //     const messagesToShare = getCurrentMessages().filter((msg) => selectedMessageIds.has(msg.id));

  //     if (messagesToShare.length === 0) {
  //       return;
  //     }

  //     onShareTargetSelectRequest?.(method, messagesToShare, groupId);

  //     exitBatchSelectMode();
  //   },
  //   [
  //     selectedMessageIds,
  //     getCurrentMessages,
  //     exitBatchSelectMode,
  //     onShareTargetSelectRequest,
  //     groupId,
  //     hasMergedForwardMessage,
  //     setShowShareMethodDialog
  //   ]
  // );

  useEffect(() => {
    if (!onBatchSelectModeChange) {
      return;
    }
    const currentUserId = getJwtObj().userId;
    const currentUserIsLeader = checkCurrentUserIsLeader();
    const selectedMessages = getCurrentMessages().filter((msg) => selectedMessageIds.has(msg.id));
    const hasOthersMessages =
      !currentUserIsLeader && selectedMessages.some((msg) => msg.user && msg.user.id !== currentUserId);
    onBatchSelectModeChange(isBatchSelectMode, selectedMessageIds.size, hasOthersMessages);
  }, [isBatchSelectMode, selectedMessageIds, getCurrentMessages, checkCurrentUserIsLeader, onBatchSelectModeChange]);

  return {
    isBatchSelectMode,
    setIsBatchSelectMode,
    selectedMessageIds,
    setSelectedMessageIds,
    toggleMessageSelection,
    exitBatchSelectMode,
    selectMsg,
    shareMsg,
    handleBatchGroupCollection,
    handleBatchRemoveGroupCollection,
    handleBatchMyCollection,
    handleBatchRemoveMyCollection,
    handleBatchDelete,
    // handleBatchShare, // 暂时隐藏转发功能
    // hasMergedForwardMessage, // 暂时隐藏转发功能
    // handleShareMethod, // 暂时隐藏转发功能
    uncollectSelectedMessages
  };
};
